import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate, Link } from "react-router-dom";
import "./AboutUs.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faShieldAlt,
    faUsers,
    faHandshake,
    faClock,
    faCheckCircle,
    faHeadset,
    faMobile,
    faFileAlt,
    faCar,
    faMotorcycle,
    faIdCard,
    faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";

function AboutUs() {
    const navigate = useNavigate();

    const coreValues = [
        {
            icon: faShieldAlt,
            title: "Trust & Security",
            description:
                "Your data is protected with industry-standard encryption. We never share your personal information without consent.",
        },
        {
            icon: faUsers,
            title: "Customer First",
            description:
                "Every service is designed with you in mind. Our dedicated agents work tirelessly to meet your vehicle service needs.",
        },
        {
            icon: faHandshake,
            title: "Transparency",
            description:
                "No hidden charges, no surprises. We believe in clear communication and upfront pricing for all our services.",
        },
        {
            icon: faClock,
            title: "Efficiency",
            description:
                "Save time with streamlined processes. What used to take days now takes hours with VaahanBuddy.",
        },
    ];

    const services = [
        {
            icon: faFileAlt,
            title: "RTO Services",
            description:
                "Complete assistance for all RTO-related work including RC transfer, NOC, address change, duplicate RC, HSRP plates, and hypothecation termination.",
        },
        {
            icon: faCar,
            title: "Car Services",
            description:
                "Buy, sell, or get instant cash on your car. We connect you with trusted platforms and provide vehicle valuation assistance.",
        },
        {
            icon: faMotorcycle,
            title: "Bike Services",
            description:
                "Complete bike-related services including buying, selling, insurance renewal, and instant cash on bike options.",
        },
        {
            icon: faIdCard,
            title: "Driving License",
            description:
                "Apply for new driving license, renewal, international driving permit, and prepare for DL tests with our comprehensive resources.",
        },
        {
            icon: faMoneyBillWave,
            title: "Loans & Finance",
            description:
                "Get vehicle loans, personal loans, instant loans, and refinance options from trusted financial partners at competitive rates.",
        },
        {
            icon: faCheckCircle,
            title: "Challan & Insurance",
            description:
                "Check and pay traffic challans online. Compare and purchase the best car and bike insurance policies instantly.",
        },
    ];

    const stats = [
        { value: "50,000+", label: "Happy Customers" },
        { value: "100+", label: "Expert Agents" },
        { value: "15+", label: "Services Offered" },
        { value: "29", label: "States Covered" },
    ];

    return (
        <div className="about-us-container">
            <Helmet>
                <title>About Us | VaahanBuddy - Your Trusted Vehicle Services Partner in India</title>
                <meta
                    name="description"
                    content="Learn about VaahanBuddy, India's leading vehicle services assistant. We provide comprehensive RTO services, insurance, loans, and vehicle documentation assistance across all Indian states."
                />
                <meta
                    name="keywords"
                    content="VaahanBuddy about, vehicle services India, RTO assistance, car insurance, bike insurance, driving license, vehicle loans, challan payment"
                />
                <meta property="og:title" content="About Us | VaahanBuddy" />
                <meta
                    property="og:description"
                    content="Your trusted partner for all vehicle-related services in India. Expert assistance for RTO work, insurance, loans, and more."
                />
                <meta property="og:url" content="https://rtoxpert.com/about" />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>

            {/* Header Section */}
            <div className="about-header">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <span className="back-arrow">←</span> Back
                </button>
                <h1>About VaahanBuddy</h1>
                <p className="header-subtitle">Your Trusted Vehicle Services Partner</p>
            </div>

            {/* Hero Section */}
            <section className="about-hero">
                <div className="hero-content">
                    <h2>Simplifying Vehicle Services Across India</h2>
                    <p>
                        VaahanBuddy is India's comprehensive vehicle services platform, designed to make all
                        your vehicle-related tasks simple, fast, and hassle-free. Whether you need to check
                        challans, renew insurance, transfer ownership, or apply for a driving license, our
                        expert agents and digital tools are here to help you every step of the way.
                    </p>
                    <p>
                        Founded with the mission to eliminate the complexity of dealing with RTOs and vehicle
                        documentation, VaahanBuddy connects vehicle owners with experienced service agents who
                        handle everything from paperwork to follow-ups, ensuring a smooth and transparent experience.
                    </p>
                </div>
            </section>

            {/* Stats Section */}
            <section className="about-stats">
                <div className="stats-container">
                    {stats.map((stat, index) => (
                        <div key={index} className="stat-item">
                            <span className="stat-value">{stat.value}</span>
                            <span className="stat-label">{stat.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Mission Section */}
            <section className="about-mission">
                <div className="mission-content">
                    <h2>Our Mission</h2>
                    <p>
                        To empower every vehicle owner in India with easy access to all vehicle-related services
                        through technology and expert assistance. We aim to save your time, reduce stress, and
                        provide transparent, reliable solutions for all your vehicle documentation and service needs.
                    </p>
                    <h2>Our Vision</h2>
                    <p>
                        To become India's most trusted and comprehensive vehicle services platform, where every
                        vehicle owner can manage all their vehicle-related tasks from a single, user-friendly
                        application—from purchasing to maintenance to documentation.
                    </p>
                </div>
            </section>

            {/* Core Values Section */}
            <section className="about-values">
                <h2>Our Core Values</h2>
                <div className="values-grid">
                    {coreValues.map((value, index) => (
                        <div key={index} className="value-card">
                            <div className="value-icon">
                                <FontAwesomeIcon icon={value.icon} />
                            </div>
                            <h3>{value.title}</h3>
                            <p>{value.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Services Overview Section */}
            <section className="about-services">
                <h2>What We Offer</h2>
                <p className="services-intro">
                    VaahanBuddy provides a comprehensive suite of vehicle services designed to cover every
                    aspect of vehicle ownership in India. Our platform combines cutting-edge technology with
                    expert human assistance to deliver seamless experiences.
                </p>
                <div className="services-grid">
                    {services.map((service, index) => (
                        <div key={index} className="service-overview-card">
                            <div className="service-icon">
                                <FontAwesomeIcon icon={service.icon} />
                            </div>
                            <h3>{service.title}</h3>
                            <p>{service.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* How It Works Section */}
            <section className="about-how-it-works">
                <h2>How VaahanBuddy Works</h2>
                <div className="steps-container">
                    <div className="step-item">
                        <div className="step-number">1</div>
                        <h3>Create Account</h3>
                        <p>
                            Sign up with just your phone number. Quick OTP verification gets you started in seconds.
                        </p>
                    </div>
                    <div className="step-item">
                        <div className="step-number">2</div>
                        <h3>Choose Service</h3>
                        <p>
                            Browse our comprehensive list of vehicle services and select what you need assistance with.
                        </p>
                    </div>
                    <div className="step-item">
                        <div className="step-number">3</div>
                        <h3>Submit Request</h3>
                        <p>
                            Fill in the required details and documents. Our experts will review and contact you.
                        </p>
                    </div>
                    <div className="step-item">
                        <div className="step-number">4</div>
                        <h3>Get Assistance</h3>
                        <p>
                            Our dedicated agents handle the entire process, keeping you updated at every step.
                        </p>
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="about-why-choose">
                <h2>Why Choose VaahanBuddy?</h2>
                <div className="why-choose-content">
                    <div className="why-choose-item">
                        <FontAwesomeIcon icon={faHeadset} className="why-icon" />
                        <div>
                            <h3>Expert Support</h3>
                            <p>
                                Our team of experienced agents understands the complexities of RTO processes and
                                vehicle documentation. They guide you through every step, ensuring accurate and
                                timely completion of your service requests.
                            </p>
                        </div>
                    </div>
                    <div className="why-choose-item">
                        <FontAwesomeIcon icon={faMobile} className="why-icon" />
                        <div>
                            <h3>Digital Convenience</h3>
                            <p>
                                Access all services from your smartphone or computer. Track your requests, store
                                vehicle documents in your Digital Garage, and receive instant notifications—all
                                from one platform.
                            </p>
                        </div>
                    </div>
                    <div className="why-choose-item">
                        <FontAwesomeIcon icon={faCheckCircle} className="why-icon" />
                        <div>
                            <h3>Pan-India Coverage</h3>
                            <p>
                                We serve customers across all 29 states and 8 union territories of India. No matter
                                where your vehicle is registered, VaahanBuddy can assist you with your service needs.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Company Info Section */}
            <section className="about-company">
                <h2>About Our Company</h2>
                <div className="company-info">
                    <p>
                        VaahanBuddy is a Brand of <strong>Raisclab Advisory LLP</strong>, a registered
                        limited Liability Partnership Firm committed to providing innovative solutions for vehicle
                        owners in India. Our platform connects users with a network of independent, experienced
                        agents who follow standard public procedures to assist with vehicle documentation and services.
                    </p>
                    <div className="disclaimer-box">
                        <h4>Important Disclaimer</h4>
                        <p>
                            VaahanBuddy is NOT affiliated with any government entity, Regional Transport Office (RTO),
                            or government authority. All services are independently provided by third-party agents
                            who follow standard public procedures. We do not represent any government body and do
                            not have any government authorization.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="about-cta">
                <h2>Ready to Get Started?</h2>
                <p>
                    Join thousands of satisfied customers who trust VaahanBuddy for their vehicle service needs.
                </p>
                <div className="cta-buttons">
                    <Link to="/" className="cta-primary">
                        Explore Services
                    </Link>
                    <Link to="/contact-form" className="cta-secondary">
                        Contact Us
                    </Link>
                </div>
            </section>
        </div>
    );
}

export default AboutUs;
